import { DecedentInfoState as _DecedentInfoState } from '@interfaces/state';
import { Action, createReducer, on } from '@ngrx/store';
import { ClearResponse, LegacyClearResponse } from '@shared/services';
import { VerifyDecedentPayload } from '@shared/services/verify-decedent/verify-decedent.service';
import * as dayjs from 'dayjs';

import * as clearActions from '../../actions/clear/clear.actions';
import * as decedentInfoActions from '../../actions/decedent-info/decedent-info.actions';
import * as decedentSecurityActions from '../../actions/decedent-security/decedent-security.actions';
import { StartOverStateCleared } from '../../actions/start-over/start-over.actions';
import * as verifyDecedentActions from '../../actions/verify-decedent/verify-decedent.actions';

export interface DecedentInfoState extends _DecedentInfoState {}

export const initialState: DecedentInfoState = {
  firstName: '',
  middleName: '',
  lastName: '',
  decedentId_private: '',
  zipCode: '',
  addressLine1: '',
  city: '',
  state: '',
  dateOfDeath: '',
  dateOfBirth: '',
  decedentPoid: '',
  isDeathVerified: false,
  deathStatusSetBy: '',
  multiplePoids: false,
  isReviewModalVisible: false,
  decedentIdExceededAttempts: false,
  inheritanceWaiverState: '',
  clearDateOfDeath: '',
  decedentValidationValid: null,
  decedentAccounts: null,
  isAdvisedClientModalShown: false,
};

const decedentInfoReducer = createReducer(
  initialState,
  on(decedentInfoActions.DecedentInfoUpdate, (state, { payload }) => ({
    ...state,
    ...payload,
  })),
  on(decedentInfoActions.IsDeathVerifiedUpdate, (state, { payload }) => ({
    ...state,
    isDeathVerified: payload.isDeathVerified,
  })),
  on(verifyDecedentActions.verifyDecedentEnd, (state, prop) => ({
    ...state,
    decedentPoid: prop.payload.recentDecedentPoid,
    isDeathVerified: prop.payload.verifiedDeceased,
    multiplePoids: prop.payload.multiplePoids,
    deathStatusSetBy: 'INTERNAL',
    inheritanceWaiverState: prop.payload.inheritanceWaiverState,
  })),
  on(decedentInfoActions.DecedentDODUpdate, (state, { payload }) => ({
    ...state,
    dateOfDeath: payload.deathDate,
  })),
  on(decedentInfoActions.DecedentCityStateUpdate, (state, { payload }) => ({
    ...state,
    addressLine1: payload.addressLine1,
    city: payload.city,
    state: payload.state,
    zipCode: payload.zipCode,
  })),
  on(clearActions.ClearEnd, (state, props) => {
    const verifiedDeceased = props.payload.CNMKongToggle
      ? (props.payload as ClearResponse).deceased
      : (props.payload as LegacyClearResponse).isDeceased;
    return {
      ...state,
      isDeathVerified: verifiedDeceased,
      deathStatusSetBy: 'EXTERNAL',
      ...(props.payload.CNMKongToggle &&
        verifiedDeceased && { clearDateOfDeath: props.payload.deathDate }),
    };
  }),
  on(verifyDecedentActions.clientSearchError, (state) => ({
    ...state,
    isDeathVerified: false,
  })),
  on(decedentInfoActions.OpenDecedentInfoReviewModal, (state) => ({
    ...state,
    isReviewModalVisible: true,
  })),
  on(decedentInfoActions.CloseDecedentInfoReviewModal, (state) => ({
    ...state,
    isReviewModalVisible: false,
  })),
  on(decedentInfoActions.DecedentIdExceededAttempts, (state) => ({
    ...state,
    decedentIdExceededAttempts: true,
  })),
  on(decedentSecurityActions.DecedentValidationSuccess, (state, { payload }) => ({
    ...state,
    decedentValidationValid: payload.valid,
  })),
  on(decedentSecurityActions.DecedentAccountsSuccess, (state, { payload }) => ({
    ...state,
    decedentAccounts: payload.accounts,
  })),
  on(decedentInfoActions.MarkAdvisedClientModalShown, (state) => ({
    ...state,
    isAdvisedClientModalShown: true,
  })),
  on(decedentInfoActions.resetDecedentInfo, () => initialState),
  on(StartOverStateCleared, () => ({ ...initialState })),
);

export function reducer(state: DecedentInfoState | undefined, action: Action) {
  return decedentInfoReducer(state, action);
}

export const getVerifyDecedentPayload = (state: DecedentInfoState): VerifyDecedentPayload => ({
  firstName: state.firstName,
  lastName: state.lastName,
  ssn_private: state.decedentId_private.replace(/-/g, ''),
  zipCode: state.zipCode,
  dateOfBirth: dayjs(state.dateOfBirth).format('YYYY-MM-DD'),
});

export const getDecedentFirstName = (state: DecedentInfoState) => state.firstName;

export const getDecedentDOD = (state: DecedentInfoState) => state.dateOfDeath;
